export type MemberTierName =
  | "Bronze"
  | "Silver"
  | "Gold"
  | "Platinum"
  | "Prestige";

export type MemberTxnType = "Earned" | "Redeemed" | "Bonus" | "Adjustment";
export type MemberTxnStatus = "Completed" | "Pending" | "Failed";

export type ProgramTier = {
  name: MemberTierName;
  order: number;
  qualifyingSpend: number;
  colorClass: string;
  badgeClass: string;
  benefits: string[];
};

export type MemberProfile = {
  memberId: string;
  fullName: string;
  email: string;
  mobile: string;
  dateOfBirth: string;
  gender: string;
  city: string;
  area: string;
  initials: string;
  programName: string;
  programId: string;
  enrollmentDate: string;
  currentTier: MemberTierName;
  availablePoints: number;
  qualifyingSpend: number;
  lifetimeSpend: number;
  totalTransactions: number;
  accountStatus: "Active" | "Blocked";
  lastLogin: string;
  password: string;
};

export type MemberTransaction = {
  id: string;
  date: string;
  time: string;
  outlet: string;
  type: MemberTxnType;
  amount: number;
  currency: string;
  points: number;
  status: MemberTxnStatus;
};

export type MemberOffer = {
  id: string;
  title: string;
  description: string;
  benefit: string;
  validUntil: string;
  eligibility: string;
  category: string;
  status: "Available" | "Expired" | "Redeemed";
  accentClass: string;
};

/** Tiers assigned to the member program (ordered). */
export const PROGRAM_TIERS: ProgramTier[] = [
  {
    name: "Bronze",
    order: 1,
    qualifyingSpend: 0,
    colorClass: "text-orange-700",
    badgeClass: "bg-orange-50 text-orange-800 border-orange-200",
    benefits: [
      "Member rates on stays",
      "Earn points on every booking",
      "Standard trip support",
    ],
  },
  {
    name: "Silver",
    order: 2,
    qualifyingSpend: 10000,
    colorClass: "text-slate-600",
    badgeClass: "bg-slate-100 text-slate-700 border-slate-200",
    benefits: [
      "1.25× points on stays and flights",
      "Member rates at checkout",
      "Priority chat support",
      "Free cancellation on select stays",
    ],
  },
  {
    name: "Gold",
    order: 3,
    qualifyingSpend: 25000,
    colorClass: "text-amber-700",
    badgeClass: "bg-amber-50 text-amber-900 border-amber-200",
    benefits: [
      "1.5× points on stays and flights",
      "Room upgrade when available",
      "Extra cabin bag on partner flights",
      "Lounge access on select routes",
    ],
  },
  {
    name: "Platinum",
    order: 4,
    qualifyingSpend: 50000,
    colorClass: "text-sky-700",
    badgeClass: "bg-sky-50 text-sky-900 border-sky-200",
    benefits: [
      "2× points on stays and flights",
      "Guaranteed late checkout",
      "Companion discount on flights",
      "Dedicated travel desk",
    ],
  },
];

export const MOCK_MEMBER: MemberProfile = {
  memberId: "1001",
  fullName: "Alex Rivera",
  email: "alex.rivera@email.com",
  mobile: "+91 98765 43210",
  dateOfBirth: "14 Mar 1992",
  gender: "Prefer not to say",
  city: "Bengaluru",
  area: "Indiranagar",
  initials: "AR",
  programName: "Perk Point Rewards",
  programId: "1",
  enrollmentDate: "14 Mar 2024",
  currentTier: "Silver",
  availablePoints: 8420,
  qualifyingSpend: 18000,
  lifetimeSpend: 62400,
  totalTransactions: 48,
  accountStatus: "Active",
  lastLogin: "Today, 10:24 AM",
  password: "",
};

export const MOCK_TRANSACTIONS: MemberTransaction[] = [
  {
    id: "TXN-104821",
    date: "13 Oct 2026",
    time: "10:24 AM",
    outlet: "City Outlet · MG Road",
    type: "Bonus",
    amount: 0,
    currency: "INR",
    points: 500,
    status: "Completed",
  },
  {
    id: "TXN-104820",
    date: "12 Oct 2026",
    time: "06:12 PM",
    outlet: "Harbor Café · Bandra",
    type: "Redeemed",
    amount: 0,
    currency: "INR",
    points: -500,
    status: "Completed",
  },
  {
    id: "TXN-104819",
    date: "11 Oct 2026",
    time: "11:40 AM",
    outlet: "Retail Store · Online",
    type: "Earned",
    amount: 4200,
    currency: "INR",
    points: 420,
    status: "Completed",
  },
  {
    id: "TXN-104818",
    date: "09 Oct 2026",
    time: "02:15 PM",
    outlet: "Fashion Hub · Phoenix",
    type: "Earned",
    amount: 6800,
    currency: "INR",
    points: 680,
    status: "Completed",
  },
  {
    id: "TXN-104817",
    date: "05 Oct 2026",
    time: "04:28 PM",
    outlet: "App Catalog",
    type: "Redeemed",
    amount: 0,
    currency: "INR",
    points: -2500,
    status: "Failed",
  },
  {
    id: "TXN-104816",
    date: "01 Oct 2026",
    time: "09:05 AM",
    outlet: "Fresh Mart · Anna Nagar",
    type: "Earned",
    amount: 2150,
    currency: "INR",
    points: 215,
    status: "Completed",
  },
  {
    id: "TXN-104815",
    date: "28 Sep 2026",
    time: "07:50 PM",
    outlet: "Program Admin",
    type: "Adjustment",
    amount: 0,
    currency: "INR",
    points: 200,
    status: "Completed",
  },
  {
    id: "TXN-104814",
    date: "22 Sep 2026",
    time: "01:20 PM",
    outlet: "Travel Desk · Terminal 3",
    type: "Earned",
    amount: 12500,
    currency: "INR",
    points: 1250,
    status: "Pending",
  },
];

export const MOCK_OFFERS: MemberOffer[] = [
  {
    id: "off-1",
    title: "Extra points on coastal stays",
    description:
      "Earn bonus points on eligible Goa hotel bookings this month.",
    benefit: "Bonus earn",
    validUntil: "31 Oct 2026",
    eligibility: "All tiers",
    category: "Earn",
    status: "Available",
    accentClass: "from-[#2563EB] to-[#F97316]",
  },
  {
    id: "off-2",
    title: "Member rate on heritage hotels",
    description:
      "Unlock a member discount on selected Jaipur and Udaipur properties.",
    benefit: "Member rate",
    validUntil: "15 Nov 2026",
    eligibility: "Silver & above",
    category: "Redeem",
    status: "Available",
    accentClass: "from-[#F97316] to-[#F59E0B]",
  },
  {
    id: "off-3",
    title: "Gold exclusive suite upgrade preview",
    description:
      "Preview of Gold suite upgrade perks — unlock when you reach Gold.",
    benefit: "Tier exclusive",
    validUntil: "31 Dec 2026",
    eligibility: "Gold & above",
    category: "Tier",
    status: "Available",
    accentClass: "from-[#0EA5E9] to-[#2563EB]",
  },
  {
    id: "off-4",
    title: "Weekend getaway bonus",
    description:
      "Bonus points for weekend mountain stays above the qualifying stay length.",
    benefit: "+250 pts",
    validUntil: "30 Sep 2026",
    eligibility: "All tiers",
    category: "Bonus",
    status: "Expired",
    accentClass: "from-slate-500 to-slate-700",
  },
];

export function tierForSpend(qualifyingSpend: number) {
  const sorted = [...PROGRAM_TIERS].sort((a, b) => a.order - b.order);
  let current = sorted[0];
  for (const tier of sorted) {
    if (qualifyingSpend >= tier.qualifyingSpend) current = tier;
  }
  return current;
}

export function getCurrentTier(tierName: MemberTierName) {
  return PROGRAM_TIERS.find((t) => t.name === tierName) ?? PROGRAM_TIERS[0];
}

export function getUpcomingTier(current: MemberTierName) {
  const currentTier = getCurrentTier(current);
  const next = PROGRAM_TIERS.find((t) => t.order === currentTier.order + 1);
  return next ?? null;
}

export function getTierProgress(
  current: MemberTierName,
  qualifyingSpend: number,
) {
  const currentTier = getCurrentTier(current);
  const upcoming = getUpcomingTier(current);
  if (!upcoming) {
    return {
      currentTier,
      upcoming: null,
      progressPct: 100,
      remaining: 0,
      target: currentTier.qualifyingSpend,
      isTop: true,
    };
  }
  const span = upcoming.qualifyingSpend - currentTier.qualifyingSpend;
  const progressed = Math.max(0, qualifyingSpend - currentTier.qualifyingSpend);
  const progressPct = Math.min(
    100,
    Math.round((progressed / (span || 1)) * 100),
  );
  return {
    currentTier,
    upcoming,
    progressPct,
    remaining: Math.max(0, upcoming.qualifyingSpend - qualifyingSpend),
    target: upcoming.qualifyingSpend,
    isTop: false,
  };
}

export function formatCurrency(amount: number, currency = "INR") {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatPts(value: number) {
  const abs = Math.abs(value).toLocaleString("en-IN");
  if (value > 0) return `+${abs}`;
  if (value < 0) return `-${abs}`;
  return abs;
}
